/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable no-console */
import { all, call, fork, put, select, takeEvery, getContext, take } from 'redux-saga/effects';
import { IRegionInterviewConfig } from 'api-paths-cas';
import { ApplicationState } from '..';
import {
  getConfig,
  getNavigationUrls,
  getPartner,
  getRegionInterviewConfig,
  saveCalendlyConfig,
  saveConfig,
  updateSFTPKey,
  getFeatureFlags,
} from '../../api/api';
import { getApplicantRecordSuccess } from '../apply/actions';
import { logout } from '../auth/actions';
import { AuthActionTypes, AuthState, UserRole } from '../auth/types';
import { StudentActionTypes, StudentState } from '../student/types';
import * as actions from './actions';
import { ConfigActionTypes } from './types';
// import { requestFilters, requestStudents } from '../dashboard/actions';
import { getAuth, getRole, getStudent } from '../selectors';
import { UIActionTypes } from '../ui/types';

// import { DEFAULT_STAGE_TAB } from '../../utilities/constants';

function* handleGetConfig(action: ReturnType<typeof actions.getConfigRecord>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleGetConfig');
      return;
    }
    const config = yield call(getConfig, idToken, action.payload.university);

    yield put(actions.getConfigRecordSuccess(config));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.getConfigRecordFailure(err.stack!));
    } else {
      yield put(actions.getConfigRecordFailure('An unknown error occured.'));
    }
  }
}

function* getConfigAfterStudentDetails() {
  /**
   * Wait until student's data appears in the store
   */
  yield take(StudentActionTypes.GET_STUDENT_RECORD_SUCCESS);

  const student: StudentState = yield select(getStudent);
  const role: UserRole = yield select(getRole);
  const auth: AuthState = yield select(getAuth);
  // determine the university based on the logged-in user's institution
  let { uni } = auth;
  if (role === UserRole.Admin || role === UserRole.Enroly) {
    // determine the university based on the student's record
    uni = student.core.pathway ? student.core.pathway : student.core.university;
  }
  yield put(actions.getConfigRecord(uni));
  yield take([ConfigActionTypes.GET_UNI_CONFIG_SUCCESS, ConfigActionTypes.GET_UNI_CONFIG_FAILURE]);
}

function* getConfigAfterApplicantDetails(action: ReturnType<typeof getApplicantRecordSuccess>) {
  /**
   * Wait until applicant's data appears in the store
   */
  yield take(StudentActionTypes.GET_APPLICATION_RECORD_SUCCESS);

  const { uni } = action.payload;
  yield put(actions.getConfigRecord(uni));
  yield take([ConfigActionTypes.GET_UNI_CONFIG_SUCCESS, ConfigActionTypes.GET_UNI_CONFIG_FAILURE]);
}

// function* handleGetConfigSuccess(action: ReturnType<typeof actions.getConfigRecord>) {
//   if (window.location.pathname.startsWith('/student')) {
//     return;
//   }
//   const params = new URLSearchParams(window.location.search);
//   if (!params.toString().length) {
//     params.set('page', '1');
//     params.set('stage', DEFAULT_STAGE_TAB);

//     console.log('handleGetConfigSuccess')
//     window.location.href = `${window.location.pathname}?${params.toString()}`;
//   }

//   yield put(requestFilters());
//   yield put(requestStudents());
// }

function* handleRefreshConfig() {
  const uniCode = yield select((state: ApplicationState) => state.config.university_code);
  yield put(actions.getConfigRecord(uniCode));
}

function* handleSaveConfig(action: ReturnType<typeof actions.saveConfig>) {
  try {
    const auth = yield select(getAuth);
    const context = yield getContext('sagaContext');
    const idToken: string = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveConfig');
      return;
    }
    const conf = yield call(saveConfig, idToken, auth.uni, action.payload);
    yield put(actions.saveConfigSuccess(conf));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveConfigFailure(err.stack!));
    } else {
      yield put(actions.saveConfigFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveCalendlyConfig(action: ReturnType<typeof actions.saveCalendlyConfig>) {
  try {
    const auth = yield select(getAuth);
    const context = yield getContext('sagaContext');
    const idToken: string = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveCalendlyConfig');
      return;
    }
    const conf = yield call(saveCalendlyConfig, idToken, auth.uni, action.payload);
    yield put(actions.saveCalendlyConfigSuccess(conf));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveCalendlyConfigFailure(err.message!));
    } else {
      yield put(actions.saveCalendlyConfigFailure('An unknown error occured.'));
    }
  }
}

function* handleImportSshPubKey(action: ReturnType<typeof actions.importSSHPubKey>) {
  try {
    const auth = yield select(getAuth);
    const context = yield getContext('sagaContext');
    const idToken: string = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleImportSshPubKey');
      return;
    }
    const response = yield call(updateSFTPKey, idToken, auth.uni, action.payload);
    yield put(actions.importSSHPubKeySuccess(response));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.importSSHPubKeyFailure(err.stack!));
    } else {
      yield put(actions.importSSHPubKeyFailure('An unknown error occured.'));
    }
  }
}

function* handleGetUniRegionInterviewConfig(action: ReturnType<typeof actions.getUniRegionInterviewConfig>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleGetUniRegionInterviewConfig');
      return;
    }
    const config: IRegionInterviewConfig[] = yield call(
      getRegionInterviewConfig,
      idToken,
      action.payload.university,
      action.payload.pathway,
    );

    yield put(actions.getUniRegionInterviewConfigSuccess(config));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.getUniRegionInterviewConfigFailure(err.stack!));
    } else {
      yield put(actions.getUniRegionInterviewConfigFailure('An unknown error occured.'));
    }
  }
}

function* handleGetPartner(action: ReturnType<typeof actions.getPartner>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleGetPartner');
      return;
    }

    const resp = yield call(getPartner, idToken, action.payload);

    yield put(actions.getPartnerByNameSuccess(resp));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.getPartnerByNameFailure(err.stack!));
    } else {
      yield put(actions.getPartnerByNameFailure('An unknown error occurred.'));
    }
  }
}

function* handleGetFeatureFlags() {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleGetFeatureFlags');
      return;
    }
    const res = yield call(getFeatureFlags, idToken);
    yield put(actions.getFeatureFlagsDataSuccess(res));
  } catch {
    yield put(actions.getFeatureFlagsDataFailure('An unknown error occurred.'));
  }
}

function* watchGetConfig() {
  yield takeEvery(ConfigActionTypes.GET_UNI_CONFIG, handleGetConfig);
}

function* watchRefreshConfig() {
  yield takeEvery(ConfigActionTypes.REFRESH_UNI_CONFIG, handleRefreshConfig);
}

// function* watchGetConfigSuccess() {
// yield takeEvery(ConfigActionTypes.GET_UNI_CONFIG_SUCCESS, handleGetConfigSuccess);
// }

function* watchSaveConfig() {
  yield takeEvery(ConfigActionTypes.SAVE_CONFIG, handleSaveConfig);
}

function* watchSaveCalendlyConfig() {
  yield takeEvery(ConfigActionTypes.SAVE_CALENDLY_CONFIG, handleSaveCalendlyConfig);
}

function* watchImportSSHPubKey() {
  yield takeEvery(ConfigActionTypes.IMPORT_SSH_PUB_KEY, handleImportSshPubKey);
}

function* watchgetUniRegionInterviewConfig() {
  yield takeEvery(ConfigActionTypes.GET_UNI_REGION_INTERVIEW_CONFIG, handleGetUniRegionInterviewConfig);
}

function* watchGetPartner() {
  yield takeEvery(ConfigActionTypes.GET_PARTNER_CONFIG_BY_NAME, handleGetPartner);
}

function* watchGetStudentsRecord() {
  yield takeEvery([StudentActionTypes.GET_STUDENT_RECORD, AuthActionTypes.TESTING], getConfigAfterStudentDetails);
}

function* watchGetApplicantRecord() {
  yield takeEvery([StudentActionTypes.GET_APPLICATION_RECORD, AuthActionTypes.TESTING], getConfigAfterApplicantDetails);
}

function* handleGetNavigationUrls(action: ReturnType<typeof actions.getNavigationUrls>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleGetNavigationUrls');
      return;
    }
    const urls = yield call(getNavigationUrls, idToken, action.payload.university);
    yield put(actions.getNavigationUrlsSuccess(urls));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.getNavigationUrlsFailure(err.stack!));
    } else {
      yield put(actions.getNavigationUrlsFailure('An unknown error occurred.'));
    }
  }
}

function* watchGetNavigationUrls() {
  yield takeEvery(ConfigActionTypes.GET_NAVIGATION_URLS, handleGetNavigationUrls);
}

function* watchGetFeatureFlags() {
  yield takeEvery(UIActionTypes.SET_CONTEXT_SUCCESS, handleGetFeatureFlags);
}

export function* handleGetAdmAllowedAliases(_action: ReturnType<typeof actions.getAdmAllowedAliases>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }

    const admConfig = yield call(getConfig, idToken, 'ADM');

    // Extract only the allowedAliases from ADM config
    const allowedAliases = admConfig?.allowedAliases || [];
    yield put(actions.getAdmAllowedAliasesSuccess(allowedAliases));
  } catch {
    // Silently fail - ADM aliases are a nice-to-have feature
    yield put(actions.getAdmAllowedAliasesFailure('Failed to fetch ADM allowed aliases'));
  }
}

function* ConfigSaga() {
  // yield all([fork(watchGetConfigSuccess)]);
  yield all([fork(watchGetConfig)]);
  yield all([fork(watchRefreshConfig)]);
  yield all([fork(watchSaveConfig)]);
  yield all([fork(watchSaveCalendlyConfig)]);
  yield all([fork(watchImportSSHPubKey)]);
  yield all([fork(watchgetUniRegionInterviewConfig)]);
  yield all([fork(watchGetPartner)]);
  yield all([fork(watchGetStudentsRecord)]);
  yield all([fork(watchGetApplicantRecord)]);
  yield all([fork(watchGetNavigationUrls)]);
  yield all([fork(watchGetFeatureFlags)]);
  yield all([takeEvery(ConfigActionTypes.GET_ADM_ALLOWED_ALIASES, handleGetAdmAllowedAliases)]);
}

export default ConfigSaga;
